"use strict";

var get = require('lodash.get');
var Cookies = require('js-cookie');
var _require = require('./decode-state'),
  decodeState = _require.decodeState;
var _require2 = require('./encode-state'),
  encodeState = _require2.encodeState;
var _require3 = require('./make-auth0-client'),
  makeAuth0Client = _require3.makeAuth0Client;
var _require4 = require('./make-refresh-token'),
  makeRefreshToken = _require4.makeRefreshToken;
var _require5 = require('./make-get-token'),
  makeGetToken = _require5.makeGetToken;
var _require6 = require('./make-get-decoded-token'),
  makeGetDecodedToken = _require6.makeGetDecodedToken;
var _require7 = require('./make-memoized-get-token'),
  makeMemoizedGetToken = _require7.makeMemoizedGetToken;
var _require8 = require('./make-is-token-expired'),
  makeIsTokenExpired = _require8.makeIsTokenExpired;
var _require9 = require('./make-authorize'),
  makeAuthorize = _require9.makeAuthorize;
var _require10 = require('./make-authenticate'),
  makeAuthenticate = _require10.makeAuthenticate;
var _require11 = require('./make-login-callback'),
  makeLoginCallback = _require11.makeLoginCallback;
var _require12 = require('./make-logout'),
  makeLogout = _require12.makeLogout;
var _require13 = require('./make-get-client'),
  makeGetClient = _require13.makeGetClient;
var _require14 = require('./make-get-user-id-from-token'),
  makeGetUserIdFromToken = _require14.makeGetUserIdFromToken;
var ORGS_NAMESPACE = 'https://ns.acloud.guru/organisationId';
var ROLES_NAMESPACE = 'https://ns.acloud.guru/roles';
var IMPERSONATOR_NAMESPACE = 'https://ns.acloud.guru/impersonatorId';
var USER_CREATED_AT_NAMESPACE = 'https://ns.acloud.guru/userCreatedAt';
var PLURALSIGHT_ID_NAMESPACE = 'https://ns.acloud.guru/pluralsightId';
var PLURALSIGHT_HANDLE_NAMESPACE = 'https://ns.acloud.guru/pluralsightHandle';

// Ensure that all functions are executed on client side (componentDidMount) as auth0 library utilizes functions in window
var makeAuthService = function makeAuthService(_ref) {
  var _ref$AUTH0_DOMAIN = _ref.AUTH0_DOMAIN,
    AUTH0_DOMAIN = _ref$AUTH0_DOMAIN === void 0 ? 'acloudguru.auth0.com' : _ref$AUTH0_DOMAIN,
    _ref$AUTH0_CUSTOM_DOM = _ref.AUTH0_CUSTOM_DOMAIN,
    AUTH0_CUSTOM_DOMAIN = _ref$AUTH0_CUSTOM_DOM === void 0 ? 'auth.acloud.guru' : _ref$AUTH0_CUSTOM_DOM,
    _ref$AUTH0_API_AUDIEN = _ref.AUTH0_API_AUDIENCE,
    AUTH0_API_AUDIENCE = _ref$AUTH0_API_AUDIEN === void 0 ? 'https://acloudguru.auth0.com/api/v2/' : _ref$AUTH0_API_AUDIEN,
    AUTH0_CLIENT_ID = _ref.AUTH0_CLIENT_ID,
    DEFAULT_REDIRECT_URI = _ref.DEFAULT_REDIRECT_URI;
  if (!DEFAULT_REDIRECT_URI) {
    throw new Error('Default Redirect URI is required. Please ensure this is listed under [Allowed Callback URLs] in the Auth0 App Settings');
  }
  if (!AUTH0_CLIENT_ID) {
    throw new Error('Auth0 Client ID is required');
  }
  var auth0DomainClient = makeAuth0Client({
    domain: AUTH0_DOMAIN,
    clientID: AUTH0_CLIENT_ID,
    audience: AUTH0_API_AUDIENCE,
    redirectUri: DEFAULT_REDIRECT_URI
  });
  var customDomainClient = makeAuth0Client({
    domain: AUTH0_CUSTOM_DOMAIN,
    clientID: AUTH0_CLIENT_ID,
    audience: AUTH0_API_AUDIENCE,
    redirectUri: DEFAULT_REDIRECT_URI
  });
  var getClient = makeGetClient({
    primaryDomainClient: customDomainClient,
    secondaryDomainClient: auth0DomainClient
  });
  var getTokenFromCookie = function getTokenFromCookie() {
    return Cookies.get('auth0_token');
  };
  var setTokenInCookie = function setTokenInCookie(token) {
    return Cookies.set('auth0_token', token, {
      secure: true,
      expires: 14
    });
  };
  var removeTokenFromCookie = function removeTokenFromCookie() {
    return Cookies.remove('auth0_token');
  };
  var isTokenExpired = makeIsTokenExpired();
  var refreshToken = makeRefreshToken({
    client: customDomainClient,
    setTokenInCookie: setTokenInCookie,
    removeTokenFromCookie: removeTokenFromCookie
  });
  var getToken = makeGetToken({
    refreshToken: refreshToken,
    getTokenFromCookie: getTokenFromCookie,
    isTokenExpired: isTokenExpired
  });
  var memoizedGetToken = makeMemoizedGetToken({
    getToken: getToken
  });
  var getDecodedToken = makeGetDecodedToken({
    getToken: memoizedGetToken
  });
  var loginCallback = makeLoginCallback({
    client: customDomainClient,
    setTokenInCookie: setTokenInCookie
  });
  var makeLogin = function makeLogin(_ref2) {
    var getClient = _ref2.getClient;
    return function (options) {
      return getClient().then(function (client) {
        return client.authorizeAsync(options);
      });
    };
  };
  var makeSignup = function makeSignup(_ref3) {
    var getClient = _ref3.getClient;
    return function (options) {
      return getClient().then(function (client) {
        var combinedOptions = Object.assign({}, options, {
          connection: 'Username-Password-Authentication'
        });
        return client.signupAsync(combinedOptions);
      });
    };
  };
  var makeSilentLogin = function makeSilentLogin(_ref4) {
    var getClient = _ref4.getClient;
    return function (options) {
      return getClient().then(function (client) {
        var combinedOptions = Object.assign({}, options, {
          realm: 'Username-Password-Authentication'
        });
        return client.loginAsync(combinedOptions);
      });
    };
  };
  return {
    signup: makeSignup({
      getClient: getClient
    }),
    silentLogin: makeSilentLogin({
      getClient: getClient
    }),
    login: makeLogin({
      getClient: getClient
    }),
    loginCallback: loginCallback,
    logout: makeLogout({
      getClient: getClient,
      removeTokenFromCookie: removeTokenFromCookie
    }),
    getToken: memoizedGetToken,
    authorize: makeAuthorize({
      ORGS_NAMESPACE: ORGS_NAMESPACE,
      ROLES_NAMESPACE: ROLES_NAMESPACE,
      getDecodedToken: getDecodedToken
    }),
    authenticate: makeAuthenticate({
      ORGS_NAMESPACE: ORGS_NAMESPACE,
      ROLES_NAMESPACE: ROLES_NAMESPACE,
      IMPERSONATOR_NAMESPACE: IMPERSONATOR_NAMESPACE,
      USER_CREATED_AT_NAMESPACE: USER_CREATED_AT_NAMESPACE,
      PLURALSIGHT_ID_NAMESPACE: PLURALSIGHT_ID_NAMESPACE,
      PLURALSIGHT_HANDLE_NAMESPACE: PLURALSIGHT_HANDLE_NAMESPACE,
      getDecodedToken: getDecodedToken
    }),
    getOrganisationIdFromToken: function getOrganisationIdFromToken() {
      return getDecodedToken().then(function (decoded) {
        return get(decoded, ORGS_NAMESPACE);
      });
    },
    getUserIdFromToken: makeGetUserIdFromToken({
      getDecodedToken: getDecodedToken
    }),
    getClient: getClient,
    decodeState: decodeState,
    encodeState: encodeState
  };
};
module.exports = {
  makeAuthService: makeAuthService
};